/**
 * The platform administrator's overview: every tenant, and what is unfinished.
 *
 * The tenant badges were built from hand-written `badge-*` class helpers, four
 * of them, which is exactly what the tone helpers and `Badge` replace.
 *
 * @module components/overview/PlatformOperations
 */

import React from "react";

import { useNavigate } from "react-router";

import { AuditEventRow } from "@/components/shared/AuditEventRow";
import { formatQueueDate } from "@/components/shared/workflow-queue-logic";
import {
  Alert,
  Badge,
  type BadgeTone,
  CollectionView,
  type Column,
  EmptyState,
  EntityCard,
  KeyValue,
  LinkButton,
  ListRow,
  MutedText,
  Panel,
  StatCard,
  StatGrid,
  StatusBadge,
  useTableSort,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";
import type { PlatformAdminOverview, PlatformTenantSummary } from "@/platform";

/** Whether a tenant has signed in yet, and whether a directory is linked. */
function onboarding(status: PlatformTenantSummary["onboardingStatus"]): {
  tone: BadgeTone;
  label: string;
} {
  if (status === "active") {
    return { tone: "success", label: "Claimed" };
  }
  if (status === "suspended") {
    return { tone: "neutral", label: "Suspended" };
  }
  return { tone: "warning", label: "Awaiting sign-in" };
}

/** Whether a tenant is configured, and how much is missing when it is not. */
function setup(tenant: PlatformTenantSummary): { tone: BadgeTone; label: string } {
  if (tenant.setupStatus === "ready") {
    return { tone: "success", label: "Ready" };
  }
  if (tenant.setupStatus === "inactive") {
    return { tone: "neutral", label: "Inactive" };
  }
  const gaps = tenant.configurationGapCodes.length;
  return { tone: "warning", label: `${gaps} gap${gaps === 1 ? "" : "s"}` };
}

export interface PlatformOperationsProps {
  overview: PlatformAdminOverview | null;
  error?: Error | null;
}

/**
 * @example
 * ```tsx
 * <PlatformOperations overview={platformAdminOverview} />
 * ```
 */
export function PlatformOperations({
  overview,
  error = null,
}: PlatformOperationsProps): React.ReactElement {
  const navigate = useNavigate();
  const gaps = overview?.configurationGapsTotal ?? 0;
  const pending = overview?.pendingTenantsTotal ?? 0;
  const unbound = overview?.unboundTenantsTotal ?? 0;

  // Every column declares a comparator, and `DataTable` draws no sort
  // control without a change handler, so without this pair they described
  // an ordering the table could never apply.
  const sort = useTableSort({ key: "tenant", direction: "asc" });

  const allColumns: readonly Column<PlatformTenantSummary>[] = [
    {
      key: "tenant",
      header: "Tenant",
      sortValue: (tenant) => tenant.name,
      render: (tenant) => (
        <div className="min-w-0">
          <LinkButton
            to={`/admin/manage/${tenant.id}`}
            variant="title"
            data-testid={`platform-tenant-link-${tenant.id}`}
          >
            {tenant.name}
          </LinkButton>
          <MutedText size="caption" as="div">
            {tenant.id}
          </MutedText>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortValue: (tenant) => tenant.status,
      render: (tenant) => <StatusBadge status={tenant.status} size="sm" />,
    },
    {
      key: "onboarding",
      header: "Onboarding",
      sortValue: (tenant) => tenant.onboardingStatus,
      render: (tenant) => (
        <div data-testid={`platform-tenant-onboarding-${tenant.id}`}>
          <Badge tone={onboarding(tenant.onboardingStatus).tone} size="sm" variant="soft">
            {onboarding(tenant.onboardingStatus).label}
          </Badge>
          {!tenant.entraTenantBound && (
            <MutedText size="caption" as="div" className="mt-1">
              No directory linked
            </MutedText>
          )}
        </div>
      ),
    },
    {
      key: "setup",
      header: "Setup",
      sortValue: (tenant) => tenant.setupStatus,
      render: (tenant) => (
        <Badge tone={setup(tenant).tone} size="sm" variant="soft">
          {setup(tenant).label}
        </Badge>
      ),
    },
    {
      key: "users",
      header: "Users",
      sortValue: (tenant) => tenant.activeUsersTotal,
      render: (tenant) => (
        <>
          <span className="font-medium">{tenant.activeUsersTotal}</span>
          <MutedText size="caption"> / {tenant.usersTotal}</MutedText>
        </>
      ),
      align: "end",
      priority: 2,
    },
    {
      key: "programmes",
      header: "Programmes",
      sortValue: (tenant) => tenant.programmesTotal,
      render: (tenant) => tenant.programmesTotal,
      align: "end",
      priority: 2,
    },
    {
      key: "activity",
      header: "Last activity",
      sortValue: (tenant) => tenant.lastActivityAt ?? null,
      render: (tenant) => formatQueueDate(tenant.lastActivityAt),
      priority: 3,
    },
  ];

  // A column where every row says the same thing is noise, and it stays
  // hidden until a tenant differs.
  const tenants = overview?.tenants ?? [];
  const varies = (value: (tenant: PlatformTenantSummary) => string | boolean | null): boolean =>
    new Set(tenants.map(value)).size > 1;
  const hidden = new Set<string>();
  if (!varies((t) => `${t.onboardingStatus}:${t.entraTenantBound}`)) {
    hidden.add("onboarding");
  }
  if (!varies((t) => t.setupStatus)) {
    hidden.add("setup");
  }
  if (!varies((t) => t.lastActivityAt ?? null)) {
    hidden.add("activity");
  }
  const columns = tenants.length > 1 ? allColumns.filter((c) => !hidden.has(c.key)) : allColumns;

  // An absent overview is not the same as no gaps, however it came to be
  // absent: "All tenants configured" rendered under the alert saying the read
  // had failed.
  const unanswered = Boolean(error) || overview === null;
  // Not answered *yet* is a skeleton; not answered because it failed is a dash.
  const loading = overview === null && !error;
  /** A count the read has not answered for is not zero. */
  const figure = (value: number): React.ReactNode => (unanswered ? "\u2014" : value);
  const allConfigured = !unanswered && gaps === 0;
  const gapsTone = gaps > 0 ? "warning" : (unanswered && "neutral") || "success";

  return (
    <div className="flex flex-col gap-6">
      {error ? <Alert tone="warning">{error.message}</Alert> : null}

      {/* No Panel around these: a StatCard is already a card, and the screen's
          own heading names them. */}
      <StatGrid columns={6} density="compact">
        {/* Active against gross, which is the distinction these two ids named
            at base: a nine-tenant estate with three live in it is not a
            healthy one, and the gross number alone cannot say which it is. */}
        <StatCard
          label="Active tenants"
          loading={loading}
          value={figure(overview?.activeTenantsTotal ?? 0)}
          total={overview?.tenantsTotal}
          data-testid="platform-tenants-total"
        />
        <StatCard
          label="Active users"
          loading={loading}
          value={figure(overview?.activeUsersTotal ?? 0)}
          total={overview?.usersTotal}
          data-testid="platform-users-total"
        />
        <StatCard
          label="Programmes"
          loading={loading}
          value={figure(overview?.programmesTotal ?? 0)}
          data-testid="platform-programmes-total"
        />
        <StatCard
          label="Configuration gaps"
          loading={loading}
          value={figure(gaps)}
          data-testid="platform-configuration-gaps-total"
        />
        <StatCard
          label="Awaiting sign-in"
          loading={loading}
          value={figure(pending)}
          data-testid="platform-pending-tenants-total"
        />
        <StatCard
          label="No directory linked"
          loading={loading}
          value={figure(unbound)}
          data-testid="platform-unbound-tenants-total"
        />
      </StatGrid>

      <Panel
        density="compact"
        title="Tenant status and usage"
        description="Shows setup, number of accounts and programme usage for each tenant."
      >
        {/* Through the collection, not a bare table: seven columns on a
            phone is seven truncated cells scrolling sideways, and every other
            list on this screen already lets the viewport pick cards. */}
        <CollectionView
          loading={loading}
          loadingLabel="Loading tenants"
          storageKey="platform-tenants"
          {...sort}
          caption="Tenant status and usage"
          columns={columns}
          rows={tenants}
          rowKey={(tenant) => tenant.id}
          rowTestId={(tenant) => `platform-tenant-row-${tenant.id}`}
          emptyState={
            <EmptyState
              icon="buildings"
              title={
                unanswered ? "The tenant list is not available yet" : "No tenants are available"
              }
              density="row"
            />
          }
          cards={{
            "data-testid": "platform-tenant-cards",
            render: (tenant) => (
              <EntityCard
                title={tenant.name}
                meta={tenant.id}
                onOpen={() => {
                  navigate(`/admin/manage/${tenant.id}`);
                }}
                data-testid={`platform-tenant-row-${tenant.id}`}
              >
                <div
                  className="flex flex-wrap items-center gap-2"
                  data-testid={`platform-tenant-onboarding-${tenant.id}`}
                >
                  <StatusBadge status={tenant.status} size="sm" />
                  <Badge tone={onboarding(tenant.onboardingStatus).tone} size="sm" variant="soft">
                    {onboarding(tenant.onboardingStatus).label}
                  </Badge>
                  <Badge tone={setup(tenant).tone} size="sm" variant="soft">
                    {setup(tenant).label}
                  </Badge>
                  {!tenant.entraTenantBound && (
                    <MutedText size="caption">No directory linked</MutedText>
                  )}
                </div>
                <KeyValue
                  className="mt-3"
                  layout="inline"
                  entries={[
                    { label: "Users", value: `${tenant.activeUsersTotal} of ${tenant.usersTotal}` },
                    { label: "Programmes", value: tenant.programmesTotal },
                    { label: "Last activity", value: formatQueueDate(tenant.lastActivityAt) },
                  ]}
                />
              </EntityCard>
            ),
          }}
          data-testid="platform-tenant-usage-table"
        />
      </Panel>

      <div className={`grid items-start gap-4 ${allConfigured ? "" : "xl:grid-cols-2"}`.trim()}>
        {allConfigured ? (
          <Alert tone="success" data-testid="platform-gaps-clear">
            Every active tenant is configured
          </Alert>
        ) : (
          <Panel
            density="compact"
            title="Configuration gaps"
            actions={
              unanswered ? null : (
                <Badge
                  tone={gapsTone}
                  size="sm"
                  variant="soft"
                  aria-label={`${gaps} configuration gap${gaps === 1 ? "" : "s"}`}
                >
                  {gaps}
                </Badge>
              )
            }
          >
            {loading ? <ListBones rows={3} label="Loading configuration gaps" /> : null}
            {!loading && unanswered ? (
              <EmptyState title="Configuration gaps are not available yet" density="row" />
            ) : null}
            {error || overview === null ? null : (
              <ul className="flex flex-col gap-2">
                {overview.configurationGaps.map((gap) => (
                  <li key={`${gap.tenantId}-${gap.code}`}>
                    <ListRow tone="default">
                      <div className="text-md font-medium">{gap.tenantName}</div>
                      <MutedText size="caption" as="div" className="mt-1">
                        {gap.label}
                      </MutedText>
                    </ListRow>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}

        <Panel density="compact" title="Recent administrative activity">
          {loading ? <ListBones rows={3} label="Loading recent activity" /> : null}
          {!loading && unanswered ? (
            <EmptyState title="Recent activity is not available yet" density="row" />
          ) : null}
          {!unanswered && overview?.recentAdministrativeEvents.length ? (
            <ul className="flex flex-col">
              {overview.recentAdministrativeEvents.map((event) => (
                <li key={`${event.timestamp}-${event.tenantId}-${event.action}-${event.target}`}>
                  <AuditEventRow
                    action={event.action}
                    actor={event.actorName}
                    subject={event.tenantName}
                    timestamp={event.timestamp}
                  />
                </li>
              ))}
            </ul>
          ) : null}
          {!unanswered && !overview?.recentAdministrativeEvents.length ? (
            <EmptyState title="No administrative activity recorded" density="row" />
          ) : null}
        </Panel>
      </div>
    </div>
  );
}
